/**
 * 导演台入口闸门对话框（计划书 D-3 的实现体）
 *
 * 三条硬性要求逐条落实：
 *  1. 必须声明影响范围（只有导演台受限，其余功能照常）—— 防用户以为整个软件坏了
 *  2. 必须给可执行的下一步（具体到点哪个开关），而不是只说"不支持"
 *  3. 必须能一键复制探测报告，便于我们回收真实环境数据
 *
 * 只有"软件渲染"这一种情形提供「仍以流畅档进入」：它是唯一"能跑但体验差"的情形，
 * 值得让用户自担风险；旧内核/无 WebGL2 给了只会让用户撞墙后更怪产品。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppCapability } from '../../app/capabilityContext';
import { formatCapabilityReport } from '../../platform/directorGate';
import { Modal } from '../../ui/Modal';

export function DirectorGateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { report, engine, guidance, gate } = useAppCapability();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  if (report === null || guidance === null || gate === null || gate.dialog === null) {
    return null;
  }

  const forceable = gate.action === 'blockWithForce';
  const d = gate.dialog;
  /**
   * 在判空之后捕获一个已收窄的常量。
   * 直接引用 report 会报错：函数声明会提升，TS 无法断定提升前的判空已发生。
   */
  const snapshot = report;

  async function copyReport(): Promise<void> {
    await navigator.clipboard.writeText(formatCapabilityReport(snapshot, engine));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={d.title}
      summary={d.scope}
      footer={
        <>
          <button type="button" className="btn-primary" onClick={onClose}>
            {d.primaryLabel}
          </button>
          {forceable ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                onClose();
                navigate('/director');
              }}
            >
              {d.forceLabel}
            </button>
          ) : null}
          <button type="button" className="btn-ghost" onClick={() => void copyReport()}>
            {copied ? '已复制' : '复制探测报告'}
          </button>
        </>
      }
    >
      <ol className="space-y-2">
        {d.steps.map((step) => (
          <li key={step.title} className="text-ui">
            <span className="font-semibold text-accent">{step.title}：</span>
            <span className="text-ink-secondary">{step.detail}</span>
          </li>
        ))}
      </ol>
      {d.fallback !== '' ? (
        <p className="mt-3 text-uiSm text-ink-muted">{d.fallback}</p>
      ) : null}
    </Modal>
  );
}
